import React, { useEffect, useState } from 'react'
import { lumi } from '../lib/lumi'
import { dedupeResponseRecords } from '../lib/offlineSurveyQueue'
import { BarChart3, FileText, Users, Clock, CheckCircle, AlertCircle, PauseCircle } from 'lucide-react'

interface SurveyRecord {
  _id: string
  status?: 'draft' | 'active' | 'paused' | 'finished'
  isDeleted?: boolean
  deletedAt?: string
}

interface ResearcherRecord {
  _id: string
  isActive?: boolean
}

interface ResponseRecord {
  _id: string
  isDeleted?: boolean
  deletedAt?: string
  status?: string
  cancellationReason?: string
}

interface DashboardStats {
  totalSurveys: number
  activeSurveys: number
  totalResearchers: number
  totalResponses: number | null
  cancelledByFilter: number | null
  surveysByStatus: {
    draft: number
    active: number
    paused: number
    finished: number
  }
}

const emptyStats: DashboardStats = {
  totalSurveys: 0,
  activeSurveys: 0,
  totalResearchers: 0,
  totalResponses: null,
  cancelledByFilter: null,
  surveysByStatus: {
    draft: 0,
    active: 0,
    paused: 0,
    finished: 0,
  },
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>(emptyStats)
  const [loading, setLoading] = useState(true)
  // Evita cargas simultaneas e guarda se o caminho rapido do total esta disponivel.
  const dashboardLoadRef = React.useRef(false)
  const responsesFastPathRef = React.useRef(true)

  useEffect(() => {
    void loadDashboardData()
  }, [])

  // Mantem o dashboard atualizado: recarrega ao voltar para a aba/janela e
  // atualiza o total de respostas periodicamente enquanto a aba estiver
  // visivel (a pesquisa concluida em outro aparelho aparece sozinha).
  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'visible') return
      if (dashboardLoadRef.current) return
      void loadDashboardData()
    }
    const pollResponses = () => {
      if (document.visibilityState !== 'visible') return
      if (dashboardLoadRef.current) return
      void loadResponsesCount(true)
    }
    const interval = window.setInterval(pollResponses, 30000)
    window.addEventListener('focus', refreshWhenVisible)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', refreshWhenVisible)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [])

  const loadResponsesCount = async (lightOnly: boolean = false) => {
    try {
      // Caminho rapido: se o servico devolve o total junto da listagem, uma
      // consulta de 1 registro substitui o download completo das respostas.
      if (responsesFastPathRef.current) {
        const quick = await lumi.entities.responses.list({ limit: 1 }) as { list?: ResponseRecord[]; total?: number }
        const quickTotal = (quick as { total?: unknown }).total
        if (typeof quickTotal === 'number' && Number.isFinite(quickTotal)) {
          setStats((previous) => ({ ...previous, totalResponses: quickTotal }))
          return
        }
      }

      // Caminho rapido indisponivel: no polling leve, evita a varredura pesada.
      if (lightOnly) return

      const responsesResponse = await lumi.entities.responses.list({ limit: 5000 })
      const responses = dedupeResponseRecords((responsesResponse.list || []) as ResponseRecord[])
        .filter((response) => !response.isDeleted && !response.deletedAt)
      // Havendo respostas excluidas, o total do servico deixa de ser confiavel
      // e a contagem passa a usar sempre a varredura completa.
      responsesFastPathRef.current = responses.length === (responsesResponse.list || []).length
      const cancelledByFilter = responses.filter((response) => (
        response.status === 'cancelled' && response.cancellationReason === 'filter'
      )).length
      setStats((previous) => ({ ...previous, totalResponses: responses.length, cancelledByFilter }))
    } catch (responsesError) {
      console.warn('Nao foi possivel carregar o total de respostas:', responsesError)
    }
  }

  const loadDashboardData = async () => {
    if (dashboardLoadRef.current) return
    dashboardLoadRef.current = true
    try {
      setLoading(true)

      const [surveysResponse, researchersResponse] = await Promise.all([
        lumi.entities.surveys.list({ limit: 1000 }),
        lumi.entities.researchers.list({ limit: 1000 }),
      ])

      const surveys = ((surveysResponse.list || []) as SurveyRecord[])
        .filter((survey) => survey.isDeleted !== true && !survey.deletedAt)
      const researchers = (researchersResponse.list || []) as ResearcherRecord[]

      const surveysByStatus = surveys.reduce(
        (acc, survey) => {
          const status = survey.status || 'draft'
          if (status in acc) {
            acc[status] += 1
          }
          return acc
        },
        {
          draft: 0,
          active: 0,
          paused: 0,
          finished: 0,
        }
      )

      setStats({
        totalSurveys: surveys.length,
        activeSurveys: surveys.filter((survey) => survey.status === 'active').length,
        totalResearchers: researchers.filter((researcher) => researcher.isActive).length,
        totalResponses: null,
        surveysByStatus,
      })
      setLoading(false)

      // Colecao pesada (cada resposta embute as respostas do questionario):
      // o contador atualiza em background, sem prender a abertura do dashboard.
      await loadResponsesCount()
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error)
      setStats(emptyStats)
    } finally {
      setLoading(false)
      dashboardLoadRef.current = false
    }
  }

  const StatCard: React.FC<{
    title: string
    value: number
    icon: React.ElementType
    color: string
  }> = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon size={24} className="text-white" />
        </div>
      </div>
    </div>
  )

  const StatusCard: React.FC<{
    count: number
    icon: React.ElementType
    color: string
    label: string
  }> = ({ count, icon: Icon, color, label }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center space-x-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{count}</p>
          <p className="text-sm text-gray-600">{label}</p>
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard Administrativo</h1>
        <button
          onClick={loadDashboardData}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          Atualizar Dados
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard
          title="Total de Pesquisas"
          value={stats.totalSurveys}
          icon={FileText}
          color="bg-blue-600"
        />
        <StatCard
          title="Pesquisas Ativas"
          value={stats.activeSurveys}
          icon={CheckCircle}
          color="bg-green-600"
        />
        <StatCard
          title="Pesquisadores Ativos"
          value={stats.totalResearchers}
          icon={Users}
          color="bg-purple-600"
        />
        <StatCard
          title="Total de Respostas"
          value={stats.totalResponses ?? '—'}
          icon={BarChart3}
          color="bg-orange-600"
        />
        <StatCard
          title="Canceladas por Filtro"
          value={stats.cancelledByFilter ?? '—'}
          icon={AlertCircle}
          color="bg-red-600"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Status das Pesquisas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatusCard
            count={stats.surveysByStatus.draft}
            icon={Clock}
            color="bg-gray-500"
            label="Rascunhos"
          />
          <StatusCard
            count={stats.surveysByStatus.active}
            icon={CheckCircle}
            color="bg-green-500"
            label="Ativas"
          />
          <StatusCard
            count={stats.surveysByStatus.paused}
            icon={PauseCircle}
            color="bg-yellow-500"
            label="Pausadas"
          />
          <StatusCard
            count={stats.surveysByStatus.finished}
            icon={AlertCircle}
            color="bg-red-500"
            label="Finalizadas"
          />
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard